import "./Illustration.css";
import React, { ImgHTMLAttributes } from "react";
import { useWebpSupport } from "../shared/WebpSupport";
import styled from "styled-components";

/*
  Generating webp versions of the PNG illustrations.
  From /public/graphics/illustrations:

  bash```
  for F in $(find . -name '*.png'); do cwebp -lossless $F -o ${F%.png}_lossless.webp; done
  for F in $(find . -name '*.png'); do cwebp -q 70 $F -o ${F%.png}_q70.webp; done
  ```
*/

export interface IllustrationAttributes {
  png: string;
  width: number;
  height: number;
  webp_q70: string;
  webp_lossless: string;
  alt: string;
}

export const ILLUSTRATIONS: { [key: string]: IllustrationAttributes } = {
  CONTRACT_TYPE_SIMPLE: {
    png: "/graphics/illustrations/contract-types/simple_400x323.png",
    width: 400,
    height: 323,
    webp_q70: "/graphics/illustrations/contract-types/simple_400x323_q70.webp",
    webp_lossless:
      "/graphics/illustrations/contract-types/simple_400x323_lossless.webp",
    alt: "Enkel kontrakt",
  },
  CONTRACT_TYPE_STANDARD: {
    png: "/graphics/illustrations/contract-types/standard_400x323.png",
    width: 400,
    height: 323,
    webp_q70:
      "/graphics/illustrations/contract-types/standard_400x323_q70.webp",
    webp_lossless:
      "/graphics/illustrations/contract-types/standard_400x323_lossless.webp",
    alt: "Standard kontrakt",
  },
  SELFIE_WOMAN: {
    png: "/graphics/illustrations/selfie_woman_400x318.png",
    width: 400,
    height: 318,
    webp_q70: "/graphics/illustrations/selfie_woman_400x318_q70.webp",
    webp_lossless: "/graphics/illustrations/selfie_woman_400x318_lossless.webp",
    alt: "Kjøper",
  },
  HAPPY_VEHICLE_BLUE: {
    png: "/graphics/illustrations/happy_vehicle_blue_400x257.png",
    width: 400,
    height: 257,
    webp_q70: "/graphics/illustrations/happy_vehicle_blue_400x257_q70.webp",
    webp_lossless:
      "/graphics/illustrations/happy_vehicle_blue_400x257_lossless.webp",
    alt: "Bil",
  },
  HAPPY_VEHICLE_YELLOW: {
    png: "/graphics/illustrations/happy_vehicle_yellow_400x258.png",
    width: 400,
    height: 258,
    webp_q70: "/graphics/illustrations/happy_vehicle_yellow_400x258_q70.webp",
    webp_lossless:
      "/graphics/illustrations/happy_vehicle_yellow_400x258_lossless.webp",
    alt: "Bil",
  },
  VEHICLE_WITH_DECOR: {
    png: "/graphics/illustrations/vehicle_with-decor_200x172.png",
    width: 200,
    height: 172,
    webp_q70: "/graphics/illustrations/vehicle_with-decor_200x172_q70.webp",
    webp_lossless:
      "/graphics/illustrations/vehicle_with-decor_200x172_lossless.webp",
    alt: "Bil",
  },
  VEHICLE_RGB: {
    png: "/graphics/illustrations/vehicle_rgb_200x112.png",
    width: 200,
    height: 112,
    webp_q70: "/graphics/illustrations/vehicle_rgb_200x112_q70.webp",
    webp_lossless: "/graphics/illustrations/vehicle_rgb_200x112_lossless.webp",
    alt: "Bil",
  },
  VEHICLE_BW: {
    png: "/graphics/illustrations/vehicle_bw_200x112.png",
    width: 200,
    height: 112,
    webp_q70: "/graphics/illustrations/vehicle_bw_200x112_q70.webp",
    webp_lossless: "/graphics/illustrations/vehicle_bw_200x112_lossless.webp",
    alt: "Bil",
  },
  MAGNIFIED_VEHICLE: {
    png: "/graphics/illustrations/magnified_vehicle_400x366.png",
    width: 400,
    height: 366,
    webp_q70: "/graphics/illustrations/magnified_vehicle_400x366_q70.webp",
    webp_lossless:
      "/graphics/illustrations/magnified_vehicle_400x366_lossless.webp",
    alt: "Tilstand",
  },
  VEHICLE_EQUIPMENT: {
    png: "/graphics/illustrations/vehicle_equipment_400x264.png",
    width: 400,
    height: 264,
    webp_q70: "/graphics/illustrations/vehicle_equipment_400x264_q70.webp",
    webp_lossless:
      "/graphics/illustrations/vehicle_equipment_400x264_lossless.webp",
    alt: "Utstyr",
  },
  ALL_TOGETHER: {
    png: "/graphics/illustrations/all_together_720x471.png",
    width: 720,
    height: 471,
    webp_q70: "/graphics/illustrations/all_together_720x471_q70.webp",
    webp_lossless: "/graphics/illustrations/all_together_720x471_lossless.webp",
    alt: "Kjøpekontrakt",
  },
  ALL_TOGETHER_WHITE_CLOUDS: {
    png: "/graphics/illustrations/all_together_whiteclouds_720x471.png",
    width: 720,
    height: 471,
    webp_q70:
      "/graphics/illustrations/all_together_whiteclouds_720x471_q70.webp",
    webp_lossless:
      "/graphics/illustrations/all_together_whiteclouds_720x471_lossless.webp",
    alt: "Kjøpekontrakt",
  },
  SELFIE_MAN: {
    png: "/graphics/illustrations/selfie_man_400x377.png",
    width: 400,
    height: 377,
    webp_q70: "/graphics/illustrations/selfie_man_400x377_q70.webp",
    webp_lossless: "/graphics/illustrations/selfie_man_400x377_lossless.webp",
    alt: "Selger",
  },
  HANDSHAKE: {
    png: "/graphics/illustrations/handshake_400x364.png",
    width: 400,
    height: 364,
    webp_q70: "/graphics/illustrations/handshake_400x364_q70.webp",
    webp_lossless: "/graphics/illustrations/handshake_400x364_lossless.webp",
    alt: "Oppgjør",
  },
  HAPPY_FACES: {
    png: "/graphics/illustrations/happy_faces_400x285.png",
    width: 400,
    height: 285,
    webp_q70: "/graphics/illustrations/happy_faces_400x285_q70.webp",
    webp_lossless: "/graphics/illustrations/happy_faces_400x285_lossless.webp",
    alt: "Forhåndsvisning",
  },
  HIGHFIVE: {
    png: "/graphics/illustrations/highfive_720x477.png",
    width: 720,
    height: 477,
    webp_q70: "/graphics/illustrations/highfive_720x477_q70.webp",
    webp_lossless: "/graphics/illustrations/highfive_720x477_lossless.webp",
    alt: "",
  },
  INVITE_HAND: {
    png: "/graphics/illustrations/invite-hand_400x271.png",
    width: 400,
    height: 271,
    webp_q70: "/graphics/illustrations/invite-hand_400x271_q70.webp",
    webp_lossless: "/graphics/illustrations/invite-hand_400x271_lossless.webp",
    alt: "Invitasjon",
  },
  SIGNATURE: {
    png: "/graphics/illustrations/signature_400x271.png",
    width: 400,
    height: 271,
    webp_q70: "/graphics/illustrations/signature_400x271_q70.webp",
    webp_lossless: "/graphics/illustrations/signature_400x271_lossless.webp",
    alt: "Signatur",
  },
  POCKETWATCH: {
    png: "/graphics/illustrations/pocketwatch_400x332.png",
    width: 400,
    height: 332,
    webp_q70: "/graphics/illustrations/pocketwatch_400x332_q70.webp",
    webp_lossless: "/graphics/illustrations/pocketwatch_400x332_lossless.webp",
    alt: "Venter",
  },
  UMBRELLA_VEHICLE: {
    png: "/graphics/illustrations/umbrella_vehicle_400x396.png",
    width: 400,
    height: 396,
    webp_q70: "/graphics/illustrations/umbrella_vehicle_400x396_q70.webp",
    webp_lossless:
      "/graphics/illustrations/umbrella_vehicle_400x396_lossless.webp",
    alt: "Forsikring",
  },
  DIGITAL_SIGNATURE: {
    png: "/graphics/illustrations/digital_signature_400x323.png",
    width: 400,
    height: 323,
    webp_q70: "/graphics/illustrations/digital_signature_400x323_q70.webp",
    webp_lossless:
      "/graphics/illustrations/digital_signature_400x323_lossless.webp",
    alt: "Digital signatur",
  },
  UMBRELLA_VEHICLE_DISCOUNT: {
    png: "/graphics/illustrations/umbrella_vehicle_discount_528x396.png",
    width: 528,
    height: 396,
    webp_q70:
      "/graphics/illustrations/umbrella_vehicle_discount_528x396_q70.webp",
    webp_lossless:
      "/graphics/illustrations/umbrella_vehicle_discount_528x396_lossless.webp",
    alt: "Forsikring",
  },
};

export interface IllustrationProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    "src" | "width" | "height"
  > {
  className?: string;
  illustration: IllustrationAttributes;
}

export const Illustration = styled(
  ({
    className,
    illustration: { alt, png, webp_q70, webp_lossless, width, height },
    ...props
  }: IllustrationProps) => {
    const support = useWebpSupport();

    const src =
      support.alpha && support.lossy && webp_q70
        ? webp_q70
        : support.alpha && support.lossless && webp_lossless
        ? webp_lossless
        : png;
    return (
      <div className={["Illustration", className].filter((c) => c).join(" ")}>
        <div
          className="Illustration-placeholder"
          style={{
            paddingTop: `${Math.round((height / width) * 100)}%`,
          }}
        />
        <img width={width} height={height} alt={alt} src={src} {...props} />
      </div>
    );
  }
)``;
